import { useEffect, useRef } from "react";
import { Table } from "react-bootstrap";
import type { DataTableProps } from "../types";
import LDSEllipsis from "../../LDS/LDSEllipsis/LDSEllipsis";
import { PaginationBar } from "./PaginationBar";
import { usePaginatedData } from "../hooks/usePaginatedData";
import { usePaginationQuery } from "../hooks/useTableQuery";

/**
 * A generic data table component that supports pagination, loading states, and custom rendering.
 *
 * @template RowT - The type of the data rows, extending a record of key-value pairs.
 *
 * @param props - The properties for the `DataTable` component.
 * @param props.columns - An array of column definitions, where each column specifies a label, accessor,
 *                        and optionally a custom render function for the cell content.
 * @param props.rowKey - A unique key for each row, which can be a string or a function that generates a key from the row data.
 * @param props.emptyMessage - A message to display when there is no data. Defaults to "No data".
 * @param props.endpoint - An optional API endpoint to fetch paginated data.
 * @param props.data - An optional array of data rows to display in the table.
 * @param props.filterQuery - An optional object representing query parameters for filtering data.
 *
 * @returns A React component that renders a table with pagination, loading, and empty state handling.
 *
 * @remarks
 * - The component uses `usePaginationQuery` to manage pagination state (e.g., current page, page size).
 * - The `usePaginatedData` hook is used to fetch and manage the paginated data, including loading and total count.
 * - The table supports custom rendering for both columns and rows.
 * - Includes a `PaginationBar` component for navigating between pages.
 */
export function DataTable<RowT extends Record<string, unknown>>(
  props: DataTableProps<RowT> & {
    endpoint?: string;
    data?: RowT[];
    filterQuery?: Record<string, string | number | boolean>;
  }
) {
  /* ───────────────────────────────  setup ─────────────────────────────── */
  const {
    columns,
    emptyMessage = "No data",
    endpoint,
    data,
    filterQuery,
  } = props;

  const { page, pageSize, setQuery } = usePaginationQuery();

  const { rows, total, loading } = usePaginatedData<RowT>({
    endpoint,
    data,
    page,
    pageSize,
    filterQuery,
  });

  // Pagination safety net:
  //  • When the filter changes (e.g. switching tabs), restart at page 1.
  //  • Otherwise, if the dataset shrank and the current page is now past the
  //    last page, clamp back to the last valid page so the table isn't empty.
  // Skipped while a server fetch is in flight (so a deep-linked page isn't reset
  // before its data loads) and on first settle (so deep links are preserved).
  const filterKey = JSON.stringify(filterQuery ?? null);
  const prevFilterKey = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;

    if (prevFilterKey.current === null) {
      prevFilterKey.current = filterKey;
    } else if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      if (page !== 1) setQuery({ page: 1 });
      return;
    }

    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) {
      setQuery({ page: lastPage });
    }
  }, [loading, filterKey, page, total, pageSize, setQuery]);

  /* ─────────────────────────────  markup ──────────────────────────────── */
  return (
    <>
      <div
        style={{
          overflowX: "auto",
          minHeight: "410px",
          maxHeight: "calc(80vh - 42px)",
        }}
      >
        <Table
          bordered
          hover
          responsive
          style={{
            flexDirection: "column",
            tableLayout: "fixed",
          }}
        >
          <thead style={{ height: 42 }}>
            <tr>
              {columns.map((col) => (
                <th key={String(col.accessor)}>{col.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {(() => {
              if (loading) {
                return renderLoadingRow();
              }
              if (rows.length === 0) {
                return renderEmptyState();
              }
              return renderTableRows();
            })()}
          </tbody>
        </Table>
      </div>
      <PaginationBar
        totalCount={total}
        setQuery={setQuery}
        page={page}
        pageSize={pageSize}
      />
    </>
  );

  function renderLoadingRow() {
    return (
      <tr>
        <td colSpan={columns.length} className="text-center">
          <LDSEllipsis color="#7f7f7f" />
        </td>
      </tr>
    );
  }

  function renderEmptyState() {
    return (
      <tr>
        <td colSpan={columns.length} className="text-center text-muted">
          {emptyMessage}
        </td>
      </tr>
    );
  }

  function renderTableRows() {
    return rows.map((row) => (
      <tr key={`row-${String(Object.values(row)[0])}`}>
        {columns.map((col) => (
          <td key={`${String(col.accessor)}`}>
            {col.render
              ? col.render(row)
              : (row[col.accessor as keyof RowT] as React.ReactNode)}
          </td>
        ))}
      </tr>
    ));
  }
}
